import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { NewRoutingRuleResult, Service, useRepositoryQuery } from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { Banner } from '@optimus-web/core';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { useCommanderSelector } from '@mews-commander/utils';
import { useAppliedRoutingRulesIds } from '../hooks/useAppliedRoutingRulesIds';
import { AddProductFormValues, ServiceOrderFormField } from './schema';

interface ProductsNotMovedByRoutingRuleBannerProps {
    service: Service;
    accountId: string;
    routingRuleResult?: NewRoutingRuleResult;
}

export const ProductsNotMovedByRoutingRuleBanner = ({
    service,
    accountId,
    routingRuleResult,
}: ProductsNotMovedByRoutingRuleBannerProps) => {
    const { watch } = useFormContext<AddProductFormValues>();
    const linkedReservationId = watch(ServiceOrderFormField.LinkedReservationId);
    const isCompany = useCommanderSelector((state) => isAccountIdCompany(state, { accountId }));

    const { data: defaultReservationsForLinking } = useRepositoryQuery(
        'billing',
        'defaultReservationsForLinking',
        { enterpriseId: service.enterpriseId, accountIds: [accountId], onlyUnpaidReservations: false },
        { enabled: !isCompany, refetchOnMount: 'always' }
    );

    const appliedRoutingRulesIds = useAppliedRoutingRulesIds({
        service,
        accountId,
        isVirtualBill: true,
        reservationIds: defaultReservationsForLinking?.map((r) => r.reservationId),
    });

    const hasProductsToMove = useMemo(
        () =>
            !!appliedRoutingRulesIds &&
            (routingRuleResult?.routingRuleAssignments || []).filter(
                (assignment) =>
                    assignment.serviceId === service.id && appliedRoutingRulesIds.includes(assignment.routingRuleId)
            ).length > 0,
        [appliedRoutingRulesIds, service.id, routingRuleResult]
    );

    if (linkedReservationId === null && hasProductsToMove) {
        return (
            <Banner
                primaryText={lt('AddServiceToReservationProductsNotProcessedByBillingAutomationBanner')}
                variant="warning"
            />
        );
    }

    return null;
};
